import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import './UploadedFileCarousel.scss';

import {
  DeleteOutlined,
  DownloadOutlined,
  LeftOutlined,
  RightOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import { Button } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Slider from 'react-slick';

import { useUiContext } from '@/contexts/UiContext';
import FileData from '@/model/General/FileData';
import { downloadFile } from '@/utils/downloadFile';

import ConfirmModal from '../ConfirmModal';
import FilePreview from './FilePreview';

interface UploadedFileCarouselProps {
  fileList: FileData[];
  setFileList?: Function;
  canDownload?: boolean;
  canRemove?: boolean;
}

const UploadedFileCarousel = (props: UploadedFileCarouselProps) => {
  const { t } = useTranslation();
  const { isMobile, isMediumScreen } = useUiContext();
  const { fileList, setFileList, canDownload = true, canRemove = true } = props;
  const settings = useMemo(() => {
    const defaultProps = {
      dots: true,
      infinite: false,
      speed: 500,
      slidesToShow: isMobile ? 1 : isMediumScreen ? 2 : 3,
      slidesToScroll: 1,
      nextArrow: <SampleNextArrow />,
      prevArrow: <SamplePrevArrow />,
    };

    return { ...defaultProps };
  }, [isMobile, isMediumScreen]);
  const sliderItems = useMemo(
    () =>
      fileList
        ? fileList
            .filter(f => !f.in_description)
            .map((file: FileData, index) => (
              <FilePreview
                key={index}
                file={file}
                canDownload={canDownload}
                actions={
                  <>
                    {canDownload && file.fileUrl && (
                      <Button
                        size="small"
                        icon={<DownloadOutlined />}
                        onClick={() => {
                          void downloadFile(file);
                        }}
                      />
                    )}
                    {setFileList && !file.removed && canRemove && (
                      <ConfirmModal
                        openButton={
                          <Button
                            className="UploadFileDeleteButton"
                            size="small"
                            icon={<DeleteOutlined />}
                          />
                        }
                        onOk={() => {
                          if (file.blobFile) {
                            setFileList(files => files.filter((_, i) => i !== index));
                          } else {
                            setFileList(files =>
                              files.map((f, i) => {
                                if (i === index) {
                                  const file = new FileData({
                                    id: f.id,
                                    title: f.title,
                                    file: f.fileUrl,
                                  });

                                  file.setRemoved(true);

                                  return file;
                                }

                                return f;
                              }),
                            );

                            return;
                          }
                        }}
                        message={t(
                          `¿Estás seguro de que deseas eliminar el archivo {{fileTitle}}?`,
                          {
                            fileTitle: file.title,
                          },
                        )}
                        title={t('Eliminar archivo')}
                      />
                    )}
                    {file.removed && (
                      <Button
                        icon={<UndoOutlined />}
                        size="small"
                        onClick={() =>
                          setFileList?.(files =>
                            files.map((f, i) => {
                              if (i === index) {
                                const file = new FileData({
                                  id: f.id,
                                  title: f.title,
                                  file: f.fileUrl,
                                });

                                file.setRemoved(false);

                                return file;
                              }

                              return f;
                            }),
                          )
                        }
                      />
                    )}
                  </>
                }
              />
            ))
        : [],

    [canDownload, fileList, setFileList, canRemove, t],
  );

  return (
    <div className="UploadedFileCarousel">
      <Slider accessibility {...settings}>
        {sliderItems}
      </Slider>
    </div>
  );
};

interface SampleArrowProps {
  className?: string;
  style?: object;
  onClick?: () => void;
}

const SampleNextArrow = (props: SampleArrowProps) => {
  const { className, style, onClick } = props;

  return (
    <RightOutlined
      className={`${className} UploadedFileCarouselNavigationButton`}
      style={{ ...style, display: 'block' }}
      onClick={onClick}
    />
  );
};

const SamplePrevArrow = (props: SampleArrowProps) => {
  const { className, style, onClick } = props;

  return (
    <LeftOutlined
      className={`${className} UploadedFileCarouselNavigationButton`}
      style={{ ...style, display: 'block' }}
      onClick={onClick}
    />
  );
};

export default UploadedFileCarousel;
